<template>
  <div
    class="licenses-card"
    :class="loadingContent ? 'loading large' : ''"
  >
    <h1 class="title">
      Profissionais
    </h1>

    <p class="licenses-summary">
      Licenças disponíveis:
      <lila-license-badge :licenses="licenses" />
      <span class="licenses-dot">·</span>
      Precisa de mais licenças?
      <a
        class="licenses-contact"
        href="#"
        @click.prevent="contactSales"
      >Entre em contato!</a>
    </p>

    <lila-message :alert-data="alert" />

    <!-- Filtros -->
    <div
      v-show="!loadingContent"
      class="licenses-filters"
    >
      <div class="licenses-filter licenses-filter--search">
        <label class="licenses-filter-label">
          E-mail ou nome do Profissional
        </label>
        <form @submit.prevent="applySearch">
          <lila-input
            v-model="filters.search"
            type="inputSearch"
            class="with-shadow"
            placeholder="Pesquisar"
            :btn-clear-search="filters.searchActive"
            @custom-clear-search-input-event="clearSearch"
          />
        </form>
      </div>

      <div class="licenses-filter">
        <label class="licenses-filter-label">Equipe</label>
        <div class="licenses-filter-picker-wrap">
          <button
            type="button"
            class="licenses-filter-select"
            :class="{ 'has-clear': filters.team }"
            @click.stop="filterPickerOpen = !filterPickerOpen"
          >
            <span :class="{ 'is-placeholder': !filters.team }">
              {{ filters.team || "Selecione" }}
            </span>
            <span
              v-if="!filters.team"
              class="licenses-filter-caret"
            />
          </button>
          <button
            v-if="filters.team"
            type="button"
            class="licenses-filter-clear"
            aria-label="Limpar filtro de equipe"
            @click.stop="applyTeamFilter(null)"
          >
            &times;
          </button>
          <div
            v-if="filterPickerOpen"
            class="licenses-filter-picker"
          >
            <team-tree-picker
              :tree="teamTree"
              :is-selectable="isTeamSelectable"
              :immediate="true"
              :current-id="filters.teamId"
              :show-current="false"
              all-label="Todas as equipes"
              title="Filtrar por equipe"
              @select="applyTeamFilter"
              @cancel="filterPickerOpen = false"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Tabela -->
    <div
      v-show="!loadingContent"
      class="licenses-table-wrapper custom-scroll"
    >
      <table class="licenses-table">
        <thead>
          <tr>
            <th>Nome do Profissional</th>
            <th>E-mail</th>
            <th>Equipe</th>
            <th class="has-text-centered">
              Ativo
            </th>
            <th class="has-text-centered">
              Editar
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in pagedRows"
            :key="row.id"
          >
            <td class="licenses-name">
              {{ row.name }}
            </td>
            <td>{{ row.email || '—' }}</td>
            <td>{{ row.team || '—' }}</td>
            <td class="has-text-centered">
              <span
                class="licenses-status"
                :class="row.isActive ? 'is-active' : 'is-inactive'"
              >
                {{ row.isActive ? 'Ativo' : 'Inativo' }}
              </span>
            </td>
            <td class="has-text-centered">
              <button
                type="button"
                class="licenses-edit-btn"
                title="Editar"
                @click="editRow(row)"
              >
                <lila-svg-icon :src="require('@assets/icons/icon_editar.svg')" />
              </button>
            </td>
          </tr>
        </tbody>
      </table>

      <p
        v-if="!loadingContent && filteredRows.length === 0"
        class="licenses-empty has-text-centered"
      >
        Nenhum profissional encontrado.
      </p>
    </div>

    <lila-pagination
      v-show="!loadingContent && filteredRows.length > 0"
      :pagination-config="pagination"
      @custom-change-page-event="changePage"
    />
  </div>
</template>

<script>
import Input from "@components/shared/fields/Input.vue";
import SvgIcon from "@components/shared/images/SvgIcon.vue";
import Message from "@components/shared/message/Message.vue";
import Pagination from "@components/shared/pagination/Pagination.vue";
import TeamTreePicker from "@components/shared/lila/TeamTreePicker.vue";
import LicenseBadge from "@components/shared/lila/LicenseBadge.vue";

export default {
  components: {
    "lila-input": Input,
    "lila-svg-icon": SvgIcon,
    "lila-message": Message,
    "lila-pagination": Pagination,
    "team-tree-picker": TeamTreePicker,
    "lila-license-badge": LicenseBadge
  },
  data() {
    return {
      loadingContent: false,
      alert: {
        show: false,
        type: "success",
        message: ""
      },
      professionals: [],
      licenses: {
        available: 0,
        total: 0,
        unlimited: false
      },
      teams: [],
      transferTargets: [],
      filterPickerOpen: false,
      filters: {
        search: "",
        searchActive: false,
        team: null,
        teamId: null
      },
      pagination: {
        current: 1,
        totalPages: 0,
        perPage: 10
      }
    };
  },
  computed: {
    // Árvore Unidades › Equipes a partir do api/team/available (com Path).
    teamTree() {
      const roots = [];
      const findOrAdd = (children, node) => {
        let existing = children.find((c) => String(c.id) === String(node.id));
        if (!existing) {
          existing = { id: node.id, name: node.name, children: [] };
          children.push(existing);
        }
        return existing;
      };
      this.transferTargets.forEach((target) => {
        let children = roots;
        (target.path || []).forEach((unit) => {
          children = findOrAdd(children, unit).children;
        });
        findOrAdd(children, target);
      });
      return roots;
    },
    filteredRows() {
      const term = this.filters.search.trim().toLowerCase();

      return this.professionals.filter((row) => {
        const matchesSearch =
          !term ||
          row.name.toLowerCase().includes(term) ||
          (row.email || "").toLowerCase().includes(term);
        const matchesTeam = !this.filters.team || row.team === this.filters.team;
        return matchesSearch && matchesTeam;
      });
    },
    pagedRows() {
      const start = (this.pagination.current - 1) * this.pagination.perPage;
      return this.filteredRows.slice(start, start + this.pagination.perPage);
    }
  },
  watch: {
    filteredRows() {
      this.recalculatePagination();
    }
  },
  created() {
    this.getLicenses();
    this.getTeams();
    this.getTransferTargets();
    this.getProfessionals();
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
  },
  methods: {
    isTeamSelectable(id) {
      return this.transferTargets.some((target) => String(target.id) === String(id));
    },

    // Unidades e equipes visíveis (escopadas) com Path para o filtro.
    async getTransferTargets() {
      try {
        const res = await this.$axios.get("api/team/available", {
          headers: this.authHeader()
        });
        const list = Array.isArray(res.data) ? res.data : res.data.Data || [];
        this.transferTargets = list.filter(Boolean).map((item) => ({
          id: this.$Utilities.getValueIgnoreCase(item, "Id"),
          name: this.$Utilities.getValueIgnoreCase(item, "Name"),
          path: (this.$Utilities.getValueIgnoreCase(item, "Path") || []).map(
            (unit) => ({
              id: this.$Utilities.getValueIgnoreCase(unit, "Id"),
              name: this.$Utilities.getValueIgnoreCase(unit, "Name")
            })
          )
        }));
      } catch (error) {
        console.error("Erro ao carregar equipes de destino:", error);
      }
    },

    // Filtro por equipe (picker immediate → id): guarda o nome em filters.team.
    applyTeamFilter(id) {
      if (id == null) {
        this.filters.team = null;
        this.filters.teamId = null;
      } else {
        const t = this.$Utilities.findTeamOrUnit(this.transferTargets, this.teams, id);
        this.filters.team = t ? t.name : null;
        this.filters.teamId = t ? id : null;
      }
      this.filterPickerOpen = false;
      this.applyClientFilters();
    },

    handleClickOutside(event) {
      if (!this.filterPickerOpen) return;
      const picker = document.querySelector(".licenses-filter-picker");
      if (picker && !picker.contains(event.target)) {
        this.filterPickerOpen = false;
      }
    },

    async getTeams() {
      try {
        const res = await this.$axios.get("api/team", {
          headers: this.authHeader()
        });
        this.teams = (res.data || []).map((item) => ({
          id: item.Id,
          name: item.Name
        }));
      } catch (error) {
        console.error("Erro ao carregar equipes:", error);
      }
    },

    async getProfessionals() {
      this.loadingContent = true;
      try {
        // Sem o filtro isActive: traz ativos e inativos (a coluna de status
        // mostra os dois). O parâmetro é nullable no back = sem filtro.
        const res = await this.$axios.get("/api/professional", {
          headers: this.authHeader()
        });
        const list = Array.isArray(res.data) ? res.data : res.data.Data || [];
        this.professionals = list
          .filter((item) => item != null)
          .map((item) => this.mapProfessional(item));
      } catch (error) {
        this.handleError(error);
      } finally {
        this.loadingContent = false;
        this.recalculatePagination();
      }
    },

    mapProfessional(item) {
      return {
        id: item.Id,
        name: item.Name,
        email: item.Email || "",
        specialty:
          this.$Utilities.getValueIgnoreCase(item, "Specialty") ||
          this.$Utilities.getValueIgnoreCase(item, "Occupation") ||
          "",
        // Profissional tem várias equipes (Teams). Resolve o rótulo como na
        // lista de Profissionais (gestão), tolerando os formatos do backend.
        team: this.formatTeams(this.extractTeamNames(item)),
        // A API devolve Status ("Active"/"Inactive"/"Pending"), não IsActive.
        isActive:
          (this.$Utilities.getValueIgnoreCase(item, "Status") ?? "Active")
            .toString()
            .toLowerCase() === "active",
        // Item bruto do backend — usado na tela de edição (não há
        // GET api/professional/{id}; os dados já vêm na listagem).
        raw: item
      };
    },

    // Extrai os nomes das equipes tolerando os formatos do backend
    // (Teams como array de objetos {Name}, array de strings, ou único Team).
    extractTeamNames(item) {
      const rawTeams =
        this.$Utilities.getValueIgnoreCase(item, "Teams") ||
        this.$Utilities.getValueIgnoreCase(item, "Team");
      if (!rawTeams) return [];
      const list = Array.isArray(rawTeams) ? rawTeams : [rawTeams];
      return list
        .map((team) => {
          if (typeof team === "string") return team;
          return team && (team.Name || team.name)
            ? team.Name || team.name
            : null;
        })
        .filter((name) => !!name);
    },

    // Rótulo das equipes: "A", "A - B", "A - B e mais N equipe(s)".
    formatTeams(teamNames) {
      if (!teamNames || teamNames.length === 0) return "";
      const visible = teamNames.slice(0, 2).join(" - ");
      const rest = teamNames.length - 2;
      return rest > 0 ? `${visible} e mais ${rest} equipe(s)` : visible;
    },

    editRow(row) {
      // Não há GET api/professional/{id}: passamos o item bruto da lista
      // para a tela de edição via sessionStorage (mesmo padrão da lista de
      // Profissionais em Gestão).
      try {
        sessionStorage.setItem(
          "lila.editProfessional",
          JSON.stringify(row.raw || {})
        );
      } catch (e) {
        // sessionStorage indisponível — a tela redireciona se não achar dados.
      }
      this.$router.push({
        name: "editProfessional",
        params: { id: String(row.id) }
      });
    },

    applySearch() {
      this.filters.searchActive = this.filters.search.trim() !== "";
      this.pagination.current = 1;
    },

    clearSearch() {
      this.filters.search = "";
      this.filters.searchActive = false;
      this.pagination.current = 1;
    },

    applyClientFilters() {
      this.pagination.current = 1;
    },

    changePage(page) {
      this.pagination.current = page;
    },

    recalculatePagination() {
      const total = Math.ceil(this.filteredRows.length / this.pagination.perPage);
      this.pagination.totalPages = total;
      if (this.pagination.current > total) {
        this.pagination.current = total || 1;
      }
      if (this.pagination.current < 1) {
        this.pagination.current = 1;
      }
    },

    async getLicenses() {
      try {
        const res = await this.$axios.get("api/Licenses", {
          headers: this.authHeader()
        });
        const data = res.data || {};

        this.licenses.unlimited = Boolean(
          this.$Utilities.getValueIgnoreCase(data, "Unlimited")
        );

        if (this.licenses.unlimited) {
          this.licenses.available = Infinity;
          this.licenses.total = Infinity;
          return;
        }

        this.licenses.available = Number(
          this.$Utilities.getValueIgnoreCase(data, "AvailableLicenses") ??
            this.$Utilities.getValueIgnoreCase(data, "Available") ??
            0
        );
        this.licenses.total = Number(
          this.$Utilities.getValueIgnoreCase(data, "TotalLicenses") ??
            this.$Utilities.getValueIgnoreCase(data, "Total") ??
            0
        );
      } catch (error) {
        console.error("Erro ao carregar licenças:", error);
      }
    },

    contactSales() {
      this.$Utilities.controlAlert(
        null,
        this.alert,
        "success",
        "Para adquirir mais licenças, entre em contato com o suporte da Lila Saúde."
      );
    },

    authHeader() {
      return {
        Authorization: `bearer ${
          this.$Session.get(this.$userSessionName).token.Value
        }`
      };
    },

    handleError(error) {
      const data = error && error.response ? error.response.data : null;
      if (error && error.response) {
        this.$Utilities.verifyToken(error.response.status);
      }
      this.$Utilities.controlAlert(
        data,
        this.alert,
        "danger",
        "Ocorreu um erro ao carregar as informações."
      );
    }
  }
};
</script>
